import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import successLogo from '../../assets/images/success.png';
import styles from './CustomerDashboard.module.css';
import Customer from '../../models/Customer';
import { getTimeout } from '../../utils/util';
import BasicInformationView from '../../components/customer/BasicInformation/BasicInformationView';
import ServiceInformationView from
      '../../components/customer/ServiceInformation/ServiceInformationView';
import RepresentativeTable from '../../components/customer/Representative/RepresentativeTable';
import CustomerSectionWrapper from
      '../../components/layout/CustomerSectionWrapper/CustomerSectionWrapper';
import AddressInformation from '../../components/customer/AddressInformation/AddressInformation';
import MifidRelatedData from '../../components/customer/MifidRelatedData/MifidRelatedData';
import PrivateBasicInformationEdit from
      '../../components/customer/BasicInformation/PrivateBasicInformationEdit';
import LegalBasicInformationEdit from
      '../../components/customer/BasicInformation/LegalBasicInformationEdit';
import ServiceInformationRegister from
      '../../components/customer/ServiceInformation/ServiceInformationRegister';
import CustomerContext from '../../context/customer-context';
import { isValidCustomer, isValidLoginName } from '../../utils/validation';
import { editCustomer } from '../../services/customer/customerService';
import APIError from '../../services/APIError';
import BreadCrumbs from '../../components/common/BreadCrumbs/BreadCrumbs';

function CustomerDashboard() {
  const { t } = useTranslation();
  const navigation = useNavigate();
  const customerCtx = useContext(CustomerContext);
  const [modifiedCustomer, setModifiedCustomer] = useState<Customer>(customerCtx.customer);
  const [isBasicInfoEdit, setIsBasicInfoEdit] = useState(false);
  const [editError, setEditError] = useState<string>('');

  useEffect(() => {
    const timer = setTimeout(() => {
      customerCtx.setSuccessMessage(undefined);
    }, getTimeout());
    return () => {
      clearTimeout(timer);
    };
  }, [customerCtx.successMessage]);

  useEffect(() => {
    setModifiedCustomer(customerCtx.customer);
  }, [customerCtx.customer]);

  const updateCustomer = (updatedCustomer: Customer) => {
    setModifiedCustomer(updatedCustomer);
  };

  const saveUpdatedCustomer = async () => {
    try {
      const withoutEmptyPortfolios = modifiedCustomer.portfolios?.filter(
        (p) => p.everisePortfolioId !== undefined,
      );
      const currentDefaultPortfolioIdAssociatedWithAccount = modifiedCustomer.portfolios?.some(
        (portfolio) => portfolio.everisePortfolioId === modifiedCustomer.defaultPortfolioId,
      );

      let { defaultPortfolioId } = modifiedCustomer;
      if (withoutEmptyPortfolios && !withoutEmptyPortfolios.length) {
        defaultPortfolioId = undefined;
      } else if (modifiedCustomer.portfolios && !modifiedCustomer.defaultPortfolioId) {
        defaultPortfolioId = Number(modifiedCustomer.portfolios[0].everisePortfolioId);
      } else if (!currentDefaultPortfolioIdAssociatedWithAccount) {
        defaultPortfolioId = modifiedCustomer.portfolios?.sort((a, b) => a.id! - b.id!)[0]
          .everisePortfolioId;
      }

      const editedCustomer = await editCustomer({
        ...modifiedCustomer,
        defaultPortfolioId,
        portfolios: withoutEmptyPortfolios,
      });
      customerCtx.setCustomer(editedCustomer);
      customerCtx.setSuccessMessage(t('customer.dashboard.editSuccessMessage'));
      setIsBasicInfoEdit(false);
      setEditError('');
      navigation('/customer');
    } catch (err) {
      if (err instanceof APIError) {
        console.log(`Failed to save customer: ${err.errorBody}`);
        setEditError(t(`customer.validation.${err.errorBody}`));
      } else {
        console.log(err);
        setEditError(t(`customer.validation.personEditFailed`));
      }
    }
  };

  const handleSave = () => {
    if (isValidCustomer(modifiedCustomer)) {
      if (!modifiedCustomer.loginName || isValidLoginName(modifiedCustomer.loginName)) {
        saveUpdatedCustomer();
      } else {
        setEditError(t('customer.validation.loginNameWrongFormat'));
      }
    } else {
      setEditError(t('customer.validation.warningMessage'));
    }
    window.scrollTo(0, 0);
  };

  const handleCancel = () => {
    setIsBasicInfoEdit(false);
    setModifiedCustomer(customerCtx.customer);
  };

  return (
    <>
      {customerCtx.successMessage && (
        <div className={styles.successMessage}>
          <img
            className={styles.logo}
            src={successLogo}
            alt={t('customer.dashboard.successLogo')}
          />
          <p>{customerCtx.successMessage}</p>
        </div>
      )}
      <BreadCrumbs />
      <div className={styles.title}>{t('customer.dashboard.title')}</div>
      {!isBasicInfoEdit && (
        <div className={styles.sectionContainer}>
          <CustomerSectionWrapper title={t('customer.basicInfo.title')}>
            <div style={{ position: 'relative' }}>
              <div className={styles.buttonContainer}>
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => setIsBasicInfoEdit(true)}
                >
                  {t('common.edit')}
                </button>
              </div>
              <BasicInformationView customer={customerCtx.customer} />
            </div>
          </CustomerSectionWrapper>
          <CustomerSectionWrapper title={t('customer.serviceInfo.title')}>
            <ServiceInformationView customer={customerCtx.customer} />
          </CustomerSectionWrapper>
        </div>
      )}
      {isBasicInfoEdit && (
        <div className={styles.sectionContainer}>
          <CustomerSectionWrapper title={t('customer.basicInfo.title')}>
            <div style={{ position: 'relative' }}>
              <div className={styles.buttonContainer}>
                <button type="button" className={styles.button} onClick={handleSave}>
                  {t('common.save')}
                </button>
                <button type="button" className={styles.button} onClick={handleCancel}>
                  {t('common.cancel')}
                </button>
              </div>
              {customerCtx.customer.personType === 'PRIVATE' ? (
                <PrivateBasicInformationEdit
                  customer={modifiedCustomer}
                  onUpdate={updateCustomer}
                  error={editError}
                />
              ) : (
                <LegalBasicInformationEdit
                  customer={modifiedCustomer}
                  onUpdate={updateCustomer}
                  error={editError}
                />
              )}
            </div>
          </CustomerSectionWrapper>
          <CustomerSectionWrapper title={t('customer.serviceInfo.title')}>
            <ServiceInformationRegister customer={modifiedCustomer} onUpdate={updateCustomer} />
          </CustomerSectionWrapper>
        </div>
      )}
      <div className={styles.sectionContainer}>
        <CustomerSectionWrapper title={t('customer.dashboard.representativeInformation.title')}>
          <RepresentativeTable customer={customerCtx.customer} />
        </CustomerSectionWrapper>
      </div>
      <div className={styles.sectionContainer}>
        <CustomerSectionWrapper title={t('customer.dashboard.addressInformation.title')}>
          <AddressInformation customer={customerCtx.customer} />
        </CustomerSectionWrapper>
      </div>
      <div className={styles.sectionContainer}>
        <CustomerSectionWrapper title={t('customer.dashboard.mifid.title')}>
          <MifidRelatedData />
        </CustomerSectionWrapper>
      </div>
    </>
  );
}

export default CustomerDashboard;
